import {ActionTree, Module, MutationTree} from "vuex";
import {RootState} from "../store"
import {Difficulty, TypesenseResponse} from "../../../../utils/models/TypesenseResponse";
import {plainToClass} from "class-transformer";
import getters from "./getters";
import graphClient from "../../../../utils/graphql/graph_client";
import {GetTrailTags} from "../../../../utils/graphql/trail-graph";
import {GetHikeExplore, GetHikeEncodedPolyline} from "../../../../utils/graphql/hikes-graph";
import {GetRecordingEncodedPolyline, GetRecordingExplore} from "../../../../utils/graphql/recorded-trail-graph";
import {bbox as turfBbox} from "@turf/turf";
import {GetCountry} from "../../../../utils/graphql/country-graph";
import {SearchableDocument, SearchableHit, SearchableType} from "../../../../utils/models/TypesenseSearchable";
import {GetRegion} from "../../../../utils/graphql/region_graph";
import {decode} from "@googlemaps/polyline-codec";
import mapPopupHelper from "../../../../utils/views/MapPopupHelper";
import mapboxgl from "mapbox-gl";
import axios from 'axios';
import startMarkerImage from "../../../../images/trail-start.svg";
import endMarkerImage from "../../../../images/trail-end.svg";
import {appCheck} from "../../../../utils/firebase/appCheck";
import {getToken} from "firebase/app-check";
import {HikeTimeUtility, PrettyTime} from "../../../../utils/helpers/HikeTime";
import {SearchAnalytics} from "../../../search/analytics/SearchAnalytics";
import {
  addStartAndEndMarkerWithLayer,
  removeStartAndEndMarkerWithLayer
} from "../../../../utils/helpers/StartEndMarkerHelper";
import {MapLayerCategory} from "../../../../utils/layer-registry";
import {
  attachMapCoverageReporting,
  CoverageMapLike
} from "../../../../utils/helpers/MapCoverageReporting";

let startMarker = null;
let endMarker = null;

export type ExploreSearchSource = 'hikes' | 'community'

export class ExploreV2State {

  defaultPadding: number = 200
  center: [number, number] = [0, 0]
  zoom: number | null = null
  animateZoom: boolean = true
  searchString: string = ""
  searchSource: ExploreSearchSource = 'hikes'
  typesenseResponse: TypesenseResponse | null = null
  typesensePageCount: number = 6
  trail: any = null
  recording: any = null
  trailEncodedPolyline: any = null
  trailEncodedPolylinePrecision: number = 6
  triggerLoading: boolean = false
  trailTags: string[] = []
  activityTags: string[] = []
  featureTags: string[] = []
  country: any | null = null
  region: any | null = null

  static distanceRangeMin = 0.0
  static distanceRangeMax = 150.0

  selectedDistanceRangeMin: number = 0.0
  selectedDistanceRangeMax: number | null = null

  static elevationRangeMin = 0.0
  static elevationRangeMax = 2500.0

  static durationRangeMin = 0.0
  static durationRangeMax = 250.0
  static communityDurationRangeMin = 0.0
  static communityDurationRangeMax = 24.0
  static communityElevationRangeMin = 0.0
  static communityElevationRangeMax = 2500.0

  selectedDurationRangeMin: number = 0.0
  selectedDurationRangeMax: number | null = null
  selectedCommunityDurationRangeMin: number = 0.0
  selectedCommunityDurationRangeMax: number = 24.0
  selectedCommunityElevationRangeMin: number = 0.0
  selectedCommunityElevationRangeMax: number = 2500.0

  selectedDifficulties: string[] = []
  selectedTrailTags: string[] = []
  selectedCommunityItemTypes: string[] = []
  selectedActivityTags: string[] = []
  selectedFeatureTags: string[] = []
  selectedElevations: string[] = []
  selectedMinRating: number | null = null
  selectedTrailGeometryTypes: string[] = []

  // Cluster navigation
  clusterLeaves: any[] = []
  clusterActiveIndex: number = 0
  clusterEncodedPolylines: Map<string, { polyline: string, precision: number }> = new Map()
  isClusterNavActive: boolean = false
}

let unclusteredFunctions: any[] = []
let clusteredFunctions: any[] = []
let enterFunctions: any[] = []
let exitFunctions: any[] = []

let enterClusteredFunctions: any[] = []
let exitClusteredFunctions: any[] = []

const actions: ActionTree<ExploreV2State, RootState> = {
  async initialiseStore(store) {
    if ($(window).width() < 600) {
      store.state.defaultPadding = 10;
    }
    store.dispatch('getTrailTags')
  },
  async getLocationFromIp(store): Promise<boolean> {
    // Returns true if map was moved, false otherwise
    try {
      // Get user's public IP then look up geolocation via HiiKER IP API
      const ipResponse = await axios.get('https://ip-api.hiiker.com/raw');
      const ip = ipResponse.data.trim();
      const apiKey = import.meta.env.VITE_HIIKER_IP_API_KEY;
      const response = await axios.get(`https://ip-api.hiiker.com/geolookup/${ip}?api_key=${apiKey}`);

      const raw = response.data;
      const entry = Array.isArray(raw) ? raw[0] : raw;
      const countryInfo = entry?.country_info || {};
      const data = {
        latitude: countryInfo['Latitude (average)'],
        longitude: countryInfo['Longitude (average)']
      };

      if (data && data.latitude && data.longitude) {
        // Assuming window.map and its flyTo method are available
        if (window.map && typeof window.map.flyTo === 'function') {
          return new Promise((resolve) => {
            // Listen for the moveend event to know when flyTo completes
            const onMoveEnd = () => {
              window.map.off('moveend', onMoveEnd);
              resolve(true);
            };
            window.map.on('moveend', onMoveEnd);
            window.map.flyTo({ center: [data.longitude, data.latitude], zoom: 6 });
          });
        } else {
          console.warn('window.map or window.map.flyTo is not available.');
          return false;
        }
      } else {
        console.error('Latitude or Longitude not found in API response:', data);
        return false;
      }
    } catch (e) {
      console.error('Error getting location from IP:', e);
      // Handle the error appropriately, e.g., fall back to a default location or notify the user.
      // You might want to check e.response for API-specific errors if available
      if (e.response) {
        console.error('API Error Status:', e.response.status);
        console.error('API Error Data:', e.response.data);
      }
      return false;
    }
  },
  async mapInitialised(store) {
    window.map.on('click', (e: mapboxgl.MapMouseEvent) => {
      // @ts-ignore
      const features = window.map.queryRenderedFeatures(e.point, { layers: ['clusters', 'unclustered-point', 'cluster-count'] });
      if (features.length == 0) {
        store.commit('setTrailEncodedPolyline', null)
        store.commit('setTrail', null)
        store.commit('setRecording', null)
        store.commit('setCountry', null)
        store.commit('clearClusterNav')
        store.dispatch('removeClusterPolylinesFromMap')
        return store.commit('setRegion', null)
      }
    })

    // Background signal only — records viewports where we rendered no trails
    // so SearchCurator::MapGapReport can tell the content team where users are
    // looking and finding nothing. Never surfaces anything to the user.
    attachMapCoverageReporting(window.map as unknown as CoverageMapLike)
  },
  async setCountryIfOnWindow(store) {
    const countryId = window.country_id
    if (countryId) {
      store.dispatch('getCountry', countryId)
      window.country_id = null
    }
  },
  async setRegionIfOnWindow(store) {
    const countryId = window.region_id
    if (countryId) {
      store.dispatch('getRegion', countryId)
      window.region_id = null
    }
  },
  async getTypesenseResponse(store) {
    // Attempt to read the bounds directly from the map.
    // If it doesn't exist (or we're not on the map page), fallback to null.
    let boundsToSearch = null;
    let currentZoom = 0;
    if (window.map && typeof window.map.getBounds === 'function') {
      // Only use bounds filtering if zoom is reasonable (> 2)
      // At very low zoom levels, the bounds cover the whole world and
      // would return global trails instead of local ones
      currentZoom = window.map.getZoom();
      if (currentZoom > 2) {
        boundsToSearch = window.map.getBounds();
      } else {
        console.warn('[Explore] Skipping bounds filter - zoom too low:', currentZoom);
      }
    }

    // Capture region-level analytics for the explore-map area search.
    // Throttled to once per browser session via SearchAnalytics so the
    // many drag/zoom-driven dispatches collapse into a single signal.
    if (window.map && typeof window.map.getCenter === 'function' && currentZoom > 2) {
      const center = window.map.getCenter();
      SearchAnalytics.recordBrowse({ lat: center.lat, lng: center.lng, scope: "explore_map" });
    }

    const isCommunitySource = store.state.searchSource === 'community'
    const queryBy = isCommunitySource
      ? ['title', 'location', 'body', 'user_display_name']
      : ['name', 'short_address', 'keywords']

    const typeSenseResponse = await TypesenseResponse.get(
      store.state.searchString,
      store.getters.toFilterString,
      boundsToSearch,
      store.state.typesensePageCount,
      100,
      {
        collection: isCommunitySource ? 'community_feed' : 'hikes',
        queryBy: queryBy,
        locationField: isCommunitySource ? 'geo_location' : 'location'
      }
    );

    // Debug logging to help diagnose the "long distance trails" bug
    if (typeSenseResponse?.hits?.length > 0) {
      const firstHit = typeSenseResponse.hits[0]?.document;
      const mapCenter = window.map?.getCenter();
      console.log('[Explore] Typesense query:', {
        zoom: currentZoom,
        source: store.state.searchSource,
        mapCenter: mapCenter ? [mapCenter.lng.toFixed(2), mapCenter.lat.toFixed(2)] : null,
        boundsUsed: !!boundsToSearch,
        resultsCount: typeSenseResponse.hits.length,
        firstResult: firstHit
          ? `${firstHit.name || firstHit.title || 'Untitled'} (${firstHit.short_address || firstHit.location || 'Unknown'})`
          : null
      });
    }

    store.commit('setTypesenseResponse', typeSenseResponse);
  },
  setClusterOnMapFromTypesenseResponse(store) {
    const map = window.map
    let typesenseResponse = plainToClass(TypesenseResponse, store.state.typesenseResponse)
    const isCommunitySource = store.state.searchSource === 'community'
    if (typesenseResponse) {
      const clusterLayer = store.getters.clusterLayer
      const clusterCountLayer = store.getters.clusterCountLayer
      const unclusteredPointLayer = store.getters.unclusteredPointLayer

      window.layerRegistry.addSource('trails', {
        type: 'geojson',
        // @ts-ignore
        data: typesenseResponse.toGeoJson(),
        cluster: true,
        clusterMaxZoom: 22,
        clusterRadius: 50
      }, true);
      window.layerRegistry.addLayer(clusterLayer, MapLayerCategory.Top)
      window.layerRegistry.addLayer(clusterCountLayer, MapLayerCategory.Top)
      window.layerRegistry.addLayer(unclusteredPointLayer, MapLayerCategory.Top, { z: 10})

      // @ts-ignore
      function clusteredPointClick(e: mapboxgl.MapMouseEvent) {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['clusters']
        });
        if (!features.length) return;

        // @ts-ignore
        const clusterId = features[0].properties.cluster_id;
        // @ts-ignore
        const pointCount = features[0].properties.point_count;
        const source = map.getSource('trails') as mapboxgl.GeoJSONSource;

        // @ts-ignore
        source.getClusterLeaves(clusterId, Math.min(pointCount, 50), 0, async (err, leaves) => {
          if (err || !leaves || leaves.length === 0) return;

          // Filter to navigable items only
          let navigableLeaves = leaves;
          if (isCommunitySource) {
            navigableLeaves = leaves.filter((leaf: any) => {
              const props = leaf.properties || {};
              const type = props.item_type || props.itemType;
              return type === 'Hike' || type === 'Recording';
            });
          }
          if (navigableLeaves.length === 0) return;

          // Clear any existing encoded polyline preview
          store.commit('setTrailEncodedPolyline', null);

          // Set up cluster navigation state
          store.commit('setClusterLeaves', navigableLeaves);
          store.commit('setClusterActiveIndex', 0);
          store.commit('setClusterNavActive', true);

          // Fetch all encoded polylines in parallel
          await store.dispatch('fetchAllClusterPolylines', navigableLeaves);

          // Display all polylines on the map
          await store.dispatch('setAllClusterPolylinesOnMap');

          // Load the first item's full data
          await store.dispatch('loadClusterItemByIndex', 0);
        });
      }

      clusteredFunctions.forEach((f) => {
        map.off('click', 'clusters', f)
      })
      clusteredFunctions = []
      clusteredFunctions.push(clusteredPointClick)

      map.off('click', 'clusters', clusteredPointClick)
      map.on('click', 'clusters', clusteredPointClick);

      // @ts-ignore
      async function unclusteredPointClick(e: mapboxgl.MapMouseEvent) {
        // Clear any active cluster navigation
        store.commit('clearClusterNav')
        store.dispatch('removeClusterPolylinesFromMap')

        const features = window.map.queryRenderedFeatures(e.point, {
          layers: ['unclustered-point']
        });
        const feature = features[0]
        // @ts-ignore
        const document = feature?.properties || {}

        if (isCommunitySource) {
          // @ts-ignore
          const itemType = document.item_type || document.itemType
          // @ts-ignore
          const rawItemId = document.item_id || document.itemId
          const itemId = Number(rawItemId)

          if (itemType === 'Hike' && !Number.isNaN(itemId)) {
            await store.dispatch('getTrail', itemId)
            return
          }

          if (itemType === 'Recording' && !Number.isNaN(itemId)) {
            await store.dispatch('getRecording', itemId)
            return
          }

          // @ts-ignore
          const itemUrl = document.item_url || document.itemUrl
          if (itemUrl) {
            window.location.href = itemUrl
          }
          return
        }

        // @ts-ignore
        const rawId = document.id
        const id = typeof rawId === 'string' && rawId.includes('_') ? rawId.split('_').pop() : rawId
        if (id !== undefined && id !== null && id !== '') {
          await store.dispatch('getTrail', Number(id))
        }
      }

      unclusteredFunctions.forEach((func) => {
        map.off('click', 'unclustered-point', func)
      })

      unclusteredFunctions = []

      unclusteredFunctions.push(unclusteredPointClick)
      map.on('click', 'unclustered-point', unclusteredPointClick)

      var popup: mapboxgl.Popup | null = null

      // @ts-ignore
      async function enterPoint(e: mapboxgl.MapMouseEvent) {
        map.getCanvas().style.cursor = 'pointer';
        const features = window.map.queryRenderedFeatures(e.point, {
          layers: ['unclustered-point']
        });

        const feature = features[0]
        // @ts-ignore
        const document = feature?.properties || {}

        if (isCommunitySource) {
          // @ts-ignore
          const itemType = document.item_type || document.itemType
          // @ts-ignore
          const rawItemId = document.item_id || document.itemId
          const itemId = Number(rawItemId)

          if (itemType === 'Hike' && !Number.isNaN(itemId)) {
            store.dispatch('getTrailEncodedPolyline', itemId)
          } else if (itemType === 'Recording' && !Number.isNaN(itemId)) {
            store.dispatch('getRecordingEncodedPolyline', itemId)
          } else {
            store.commit('setTrailEncodedPolyline', null)
          }
          return
        }

        // @ts-ignore
        const id = document.id
        store.dispatch('getTrailEncodedPolyline', id)
      }

      // @ts-ignore
      async function exitPoint(e: mapboxgl.MapMouseEvent) {
        map.getCanvas().style.cursor = '';
        setTimeout(() => {
          store.commit('setTrailEncodedPolyline', null)
        }, 500)
        popup?.remove()
      }

      // @ts-ignore
      function enterClusteredPoint() {
        map.getCanvas().style.cursor = 'pointer';
      }

      // @ts-ignore
      function exitClusteredPoint() {
        map.getCanvas().style.cursor = '';
      }

      enterClusteredFunctions.forEach((f) => {
        map.off('mouseenter', 'clusters', f)
      })
      enterClusteredFunctions = []
      enterClusteredFunctions.push(enterClusteredPoint)

      map.on('mouseenter', 'clusters', enterClusteredPoint);

      exitClusteredFunctions.forEach((f) => {
        map.off('mouseleave', 'clusters', f)
      })
      exitClusteredFunctions = []
      exitClusteredFunctions.push(exitClusteredPoint)

      map.on('mouseleave', 'clusters', exitClusteredPoint);

      enterFunctions.forEach((f) => {
        map.off('mouseenter', 'unclustered-point', f)
      })
      enterFunctions = []
      enterFunctions.push(enterPoint)

      if(window.innerWidth > 600){
        map.on('mouseenter', 'unclustered-point', enterPoint);
      }


      exitFunctions.forEach((f) => {
        map.off('mouseleave', 'unclustered-point', f)
      })
      exitFunctions = []
      exitFunctions.push(exitPoint)


      map.on('mouseleave', 'unclustered-point', exitPoint);
    }
  },
  async easeToCenter(store, center: [number, number]) {
    store.commit('setCenter', center)
    const map = window.map
    map.jumpTo({
      center: center,
      zoom: 9
    });
  },
  async getTrailEncodedPolyline(store, id: number) {
    let headers = {};
    try {
      const appCheckTokenResponse = await getToken(appCheck, false);
      headers = { 'X-Firebase-AppCheck': appCheckTokenResponse.token }
    } catch (e) {
      console.error(e);
    }
    let response = await graphClient.request(GetHikeEncodedPolyline,
      {id: id}, headers,
    )
    store.commit('setTrailEncodedPolylinePrecision', 6)
    await store.commit('setTrailEncodedPolyline', response.hikeOverviewPolyline)
  },
  async getRecordingEncodedPolyline(store, id: number) {
    let headers = {};
    try {
      const appCheckTokenResponse = await getToken(appCheck, false);
      headers = { 'X-Firebase-AppCheck': appCheckTokenResponse.token }
    } catch (e) {
      console.error(e);
    }
    let response = await graphClient.request(GetRecordingEncodedPolyline,
      {id: id}, headers,
    )
    store.commit('setTrailEncodedPolylinePrecision', 5)
    await store.commit('setTrailEncodedPolyline', response.recording?.encodedPolyline ?? null)
  },
  unclusteredPointClick(store, e: mapboxgl.MapMouseEvent) {
  },
  async getTrail(store, id: number) {
    try {
      store.commit('toggleTriggerLoading')
      let headers = {};
      try {
        const appCheckTokenResponse = await getToken(appCheck, false);
        headers = { 'X-Firebase-AppCheck': appCheckTokenResponse.token }
      } catch (e) {
        console.error(e);
      }
      let hikeRequest = await graphClient.request(GetHikeExplore,
        {id: id}, headers,
      )
      store.commit('setRecording', null)
      store.commit('setTrail', hikeRequest.hike)
    } catch (e) {
      // error
      console.error(e)
    }
  },
  async getRecording(store, id: number) {
    try {
      store.commit('toggleTriggerLoading')
      let headers = {};
      try {
        const appCheckTokenResponse = await getToken(appCheck, false);
        headers = { 'X-Firebase-AppCheck': appCheckTokenResponse.token }
      } catch (e) {
        console.error(e);
      }
      let recordingRequest = await graphClient.request(GetRecordingExplore,
        {id: id}, headers,
      )
      store.commit('setTrail', null)
      store.commit('setRecording', recordingRequest.recording)
    } catch (e) {
      console.error(e)
    }
  },
  async setTrailEncodedPolylineOnMap(store) {
    const map = window.map
    const encodedPolyline = store.state.trailEncodedPolyline
    const precision = store.state.trailEncodedPolylinePrecision

    if (encodedPolyline) {
      // Decode the polyline string to coordinates
      // decode() returns [[lat, lon], ...] so we need to swap to [[lon, lat], ...]
      const decodedCoordinates = decode(encodedPolyline, precision).map(([lat, lon]) => [lon, lat]);
      
      if (decodedCoordinates.length > 0) {
        // Create a GeoJSON LineString from the decoded coordinates
        const geojson = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: decodedCoordinates
              },
              properties: {
                routeStyle: 'route'
              }
            },
            // Add start marker
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: decodedCoordinates[0]
              },
              properties: {
                markerType: 'start'
              }
            },
            // Add end marker
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: decodedCoordinates[decodedCoordinates.length - 1]
              },
              properties: {
                markerType: 'end'
              }
            }
          ]
        };
        
        const trailLayer = await store.dispatch('getEncodedPolylineLayer')
        window.layerRegistry.removeLayer('trail-encoded-polyline')
        window.layerRegistry.removeLayer('trail-encoded-polyline-markers')

        // Add the source with both routes and markers
        window.layerRegistry.addSource('trail-encoded-polyline', {
          type: "geojson",
          // @ts-ignore
          data: geojson,
        }, true)

        // Add the line layer (will only render LineString features)
        window.layerRegistry.addLayer(trailLayer, MapLayerCategory.Top)

        // Add start and end markers as a symbol layer (will only render Point features)
        window.layerRegistry.addLayer({
          id: 'trail-encoded-polyline-markers',
          type: 'symbol',
          source: 'trail-encoded-polyline',
          filter: ['==', '$type', 'Point'],
          layout: {
            'icon-image': [
              'case',
              ['==', ['get', 'markerType'], 'start'],
              'hike-control-point-start',
              'hike-control-point-end'
            ],
            'icon-size': 0.25,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true
          }
        }, MapLayerCategory.Top);
      }
    } else {
      // No polyline to display, just clean up any existing layers
      store.dispatch('removeTrailEncodedPolylineFromMap')
    }
  },
  removeTrailEncodedPolylineFromMap(store) {
    const map = window.map
    window.layerRegistry.removeLayer('trail-encoded-polyline')
    window.layerRegistry.removeLayer('trail-encoded-polyline-markers')
    if (startMarker) {
      startMarker.remove();
      startMarker = null;
    }
    if (endMarker) {
      endMarker.remove();
      endMarker = null;
    }
  },
  async setTrailOnMap(store, skipBounds: boolean = false) {
    const map = window.map
    const trail = store.state.trail
    store.dispatch('removeTrailFromMap')
    store.dispatch('removeCountryFromMap')
    if (trail) {
      // Get the full FeatureCollection
      const trailGeoJson = store.getters.getTrailGeoJsonFeature

      // Add the source with the full FeatureCollection
      window.layerRegistry.addSource('trail', {
        type: "geojson",
        data: trailGeoJson,
      }, true)

      // Add line layer for the MultiLineString features
      const trailLayer = await store.dispatch('getTrailLayer')
      // Filter to only show line features (both LineString and MultiLineString)
      trailLayer.filter = ['==', '$type', 'LineString']
      window.layerRegistry.addLayer(trailLayer, MapLayerCategory.Annotations)

      // Add symbol layer for start/end points using sprite sheet images
      const markerLayer = {
        id: 'trail-markers',
        type: 'symbol',
        source: 'trail',
        filter: ['==', '$type', 'Point'],
        layout: {
          'icon-image': [
            'case',
            ['==', ['get', 'markerType'], 'start'],
            'hike-control-point-start',  // Use sprite sheet image for start
            'hike-control-point-end'      // Use sprite sheet image for end
          ],
          'icon-size': 0.25,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      }
      window.layerRegistry.addLayer(markerLayer, MapLayerCategory.Annotations)

      if (!skipBounds) {
        // Use the MultiLineString feature for bounds
        const multiLineString = store.getters.getTrailMultiLineString
        await store.dispatch('setMapBoundsFromPolygonGeojson', { geoJson: multiLineString, duration: 0 })
      }
      await store.dispatch('addPolylineArrowLayerToMap')
    }
  },
  async setRecordingOnMap(store) {
    const recording = store.state.recording
    store.dispatch('removeTrailFromMap')
    store.dispatch('removeCountryFromMap')
    if (recording) {
      const recordingGeoJson = store.getters.getRecordingGeoJsonFeature
      if (!recordingGeoJson) return

      window.layerRegistry.addSource('trail', {
        type: "geojson",
        data: recordingGeoJson,
      }, true)

      const trailLayer = await store.dispatch('getTrailLayer')
      trailLayer.filter = ['==', '$type', 'LineString']
      window.layerRegistry.addLayer(trailLayer, MapLayerCategory.Annotations)

      const markerLayer = {
        id: 'trail-markers',
        type: 'symbol',
        source: 'trail',
        filter: ['==', '$type', 'Point'],
        layout: {
          'icon-image': [
            'case',
            ['==', ['get', 'markerType'], 'start'],
            'hike-control-point-start',
            'hike-control-point-end'
          ],
          'icon-size': 0.25,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      }
      window.layerRegistry.addLayer(markerLayer, MapLayerCategory.Annotations)

      // Fit map to recording bounds
      const lineFeature = recordingGeoJson.features?.find((f: any) => f.geometry?.type === 'LineString')
      if (lineFeature) {
        await store.dispatch('setMapBoundsFromPolygonGeojson', { geoJson: lineFeature, duration: 0 })
      }
      await store.dispatch('addPolylineArrowLayerToMap')
    }
  },
  removeTrailFromMap(store) {
    const map = window.map
    window.layerRegistry.removeLayer('trail')
    window.layerRegistry.removeLayer('trail-markers')
    window.layerRegistry.removeLayer('trail-arrow')
    window.layerRegistry.removeLayer('trail-encoded-polyline')
    window.layerRegistry.removeLayer('trail-encoded-polyline-markers')
    if (startMarker) {
      startMarker.remove();
      startMarker = null;
    }
    if (endMarker) {
      endMarker.remove();
      endMarker = null;
    }
    removeStartAndEndMarkerWithLayer()
  },
  async setCountryOnMap(store) {
    const map = window.map
    const country = store.state.country
    await store.dispatch('removeTrailFromMap')
    await store.dispatch('removeCountryFromMap')
    if (country) {
      const countryLayer = await store.dispatch('getCountryLayer')
      if (country.polygon) {
        window.layerRegistry.addSource('country', {
          type: "geojson",
          // @ts-ignore
          data: store.getters.getCountryGeoJsonFeature,
        })
        window.layerRegistry.addLayer(countryLayer, MapLayerCategory.Annotations)
      }
      if (country.boundingBox) {
        await store.dispatch('setMapBoundsFromBoundingBox', country.boundingBox)
      }
    }
  },
  removeCountryFromMap(store) {
    const map = window.map
    if (map.getLayer('country')) {
      map.removeLayer('country')
    }
    if (map.getSource('country')) {
      map.removeSource('country')
    }
  },
  async setRegionOnMap(store) {
    const map = window.map
    const country = store.state.region
    if (map.getLayer('country')) {
      map.removeLayer('country')
    }
    if (map.getSource('country')) {
      map.removeSource('country')
    }
    if (country) {
      const countryLayer = await store.dispatch('getCountryLayer')
      map.addSource('country', {
        type: "geojson",
        // @ts-ignore
        data: store.getters.getRegionGeoJsonFeature,
      })
      map.addLayer(countryLayer, 'clusters')
      const geoJson = store.getters.getRegionGeoJsonFeature
      if (geoJson) {
        await store.dispatch('setMapBoundsFromPolygonGeojson', { geoJson: store.getters.getRegionGeoJsonFeature, duration: 0 })
      } else {
        store.dispatch('easeToCenter', [country.lonlatCoords[0], country.lonlatCoords[1]])
      }
    }
  },
  addPolylineArrowLayerToMap(
    store,
  ) {
    const arrowLayer = 'trail-arrow'
    const targetLayer = 'trail'
    const map = window.map
    const url = "https://i.postimg.cc/3JMHRKJ3/polyline-arrow-symbol.png";
    map.loadImage(url, (error?: Error | undefined, result?: HTMLImageElement | ImageBitmap | undefined) => {
      if (error) {
        return;
      }
      if (map.hasImage(arrowLayer)) {
        map.removeImage(arrowLayer);
      }
      map.addImage(arrowLayer, result as ImageBitmap);
      if (map.getLayer(arrowLayer)) {
        map.removeLayer(arrowLayer);
      }
      window.layerRegistry.addLayer({
        id: arrowLayer,
        type: "symbol",
        source: targetLayer,
        layout: {
          "symbol-placement": "line",
          "icon-allow-overlap": true,
          'icon-ignore-placement': true,
          "icon-image": arrowLayer,
          "icon-size": 0.045,
          visibility: "visible",
        },
      }, MapLayerCategory.Annotations, { z: 10 })
    });
  },
  setMapBoundsFromPolygonGeojson(store, data: { geoJson: GeoJSON.Feature, duration: number }) {
    const map = window.map
    const newBbox = turfBbox(data.geoJson);
    // @ts-ignore
    const bounds = mapboxgl.LngLatBounds.convert(newBbox);
    map.fitBounds(bounds, { padding: store.state.defaultPadding });
  },
  setMapBoundsFromBoundingBox(store, bounds: [number, number, number, number]) {
    const map = window.map
    map.fitBounds(bounds, { padding: store.state.defaultPadding });
  },
  async getTrailLayer(store): Promise<mapboxgl.LineLayer> {

    // Return the trail layer configuration
    return {
      id: 'trail',
      type: "line",
      source: 'trail',
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": "#DB2B35",
        "line-width": 5,
      }
    };
  },
  async getEncodedPolylineLayer(store): Promise<mapboxgl.LineLayer> {
    return {
      id: 'trail-encoded-polyline',
      type: "line",
      source: 'trail-encoded-polyline',
      filter: ['==', '$type', 'LineString'], // MultiLineString is treated as LineString for $type
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": "#DB2B35",
        "line-width": 5
      }
    }
  },
  async getCountryLayer(store): Promise<mapboxgl.FillLayer> {
    return {
      id: 'country',
      type: "fill",
      source: 'country',
      paint: {
        "line-color": "#F57700",
        "line-width": 5,
        "fill-color": "#F57700",
        "fill-opacity": 0.20,
      }
    }
  },
  async getTrailTags(store) {
    try {
      let tagsResponse = await graphClient.request(GetTrailTags)
      let tags = tagsResponse.tags.map(t => t.name).filter(onlyUnique)
      store.commit('setTrailTags', tags)
    } catch (e) {

    }
  },
  async getCountry(store, countryId: number) {
    try {
      let countryResponse = await graphClient.request(GetCountry, { id: countryId })
      store.commit('setCountry', countryResponse.country)
    } catch (e) {

    }
  },
  async getRegion(store, regionId: number) {
    try {
      let regionResponse = await graphClient.request(GetRegion, { id: regionId })
      store.commit('setRegion', regionResponse.region)
    } catch (e) {

    }
  },
  async fetchAllClusterPolylines(store, leaves: any[]) {
    let headers = {};
    try {
      const appCheckTokenResponse = await getToken(appCheck, false);
      headers = { 'X-Firebase-AppCheck': appCheckTokenResponse.token }
    } catch (e) {
      console.error(e);
    }

    const isCommunitySource = store.state.searchSource === 'community'

    const promises = leaves.map(async (leaf) => {
      const props = leaf.properties || {}
      let itemType: string
      let itemId: number

      if (isCommunitySource) {
        itemType = props.item_type || props.itemType
        itemId = Number(props.item_id || props.itemId)
      } else {
        itemType = 'Hike'
        const rawId = props.id
        const id = typeof rawId === 'string' && rawId.includes('_') ? rawId.split('_').pop() : rawId
        itemId = Number(id)
      }

      if (Number.isNaN(itemId)) return
      const key = `${itemType}-${itemId}`

      try {
        if (itemType === 'Hike') {
          const response = await graphClient.request(GetHikeEncodedPolyline, { id: itemId }, headers)
          if (response.hikeOverviewPolyline) {
            store.commit('addClusterEncodedPolyline', { key, polyline: response.hikeOverviewPolyline, precision: 6 })
          }
        } else if (itemType === 'Recording') {
          const response = await graphClient.request(GetRecordingEncodedPolyline, { id: itemId }, headers)
          if (response.recording?.encodedPolyline) {
            store.commit('addClusterEncodedPolyline', { key, polyline: response.recording.encodedPolyline, precision: 5 })
          }
        }
      } catch (e) {
        console.error(`Failed to fetch polyline for ${key}:`, e)
      }
    })

    await Promise.allSettled(promises)
  },
  setAllClusterPolylinesOnMap(store) {
    const leaves = store.state.clusterLeaves
    const activeIndex = store.state.clusterActiveIndex
    const isCommunitySource = store.state.searchSource === 'community'

    // Remove existing cluster polyline layers
    store.dispatch('removeClusterPolylinesFromMap')

    const features: any[] = []
    leaves.forEach((leaf, index) => {
      const props = leaf.properties || {}
      let itemType: string
      let itemId: number

      if (isCommunitySource) {
        itemType = props.item_type || props.itemType
        itemId = Number(props.item_id || props.itemId)
      } else {
        itemType = 'Hike'
        const rawId = props.id
        const id = typeof rawId === 'string' && rawId.includes('_') ? rawId.split('_').pop() : rawId
        itemId = Number(id)
      }

      const key = `${itemType}-${itemId}`
      const cached = store.state.clusterEncodedPolylines.get(key)
      if (!cached) return

      const decodedCoordinates = decode(cached.polyline, cached.precision).map(([lat, lon]) => [lon, lat])
      if (decodedCoordinates.length === 0) return

      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: decodedCoordinates
        },
        properties: {
          isActive: index === activeIndex,
          leafIndex: index
        }
      })
    })

    if (features.length === 0) return

    const geojson = {
      type: 'FeatureCollection',
      features
    }

    window.layerRegistry.addSource('cluster-polylines', {
      type: 'geojson',
      // @ts-ignore
      data: geojson,
    }, true)

    // Layer 1: White border for active route (bottom)
    window.layerRegistry.addLayer({
      id: 'cluster-polyline-active-border',
      type: 'line',
      source: 'cluster-polylines',
      filter: ['==', ['get', 'isActive'], true],
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#ffffff',
        'line-width': 9,
      }
    }, MapLayerCategory.Top)

    // Layer 2: Inactive routes (semi-transparent)
    window.layerRegistry.addLayer({
      id: 'cluster-polyline-inactive',
      type: 'line',
      source: 'cluster-polylines',
      filter: ['==', ['get', 'isActive'], false],
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#DB2B35',
        'line-width': 3,
        'line-opacity': 0.4,
      }
    }, MapLayerCategory.Top)

    // Layer 3: Active route (on top of white border)
    window.layerRegistry.addLayer({
      id: 'cluster-polyline-active',
      type: 'line',
      source: 'cluster-polylines',
      filter: ['==', ['get', 'isActive'], true],
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#DB2B35',
        'line-width': 5,
      }
    }, MapLayerCategory.Top)
  },
  removeClusterPolylinesFromMap() {
    window.layerRegistry.removeLayer('cluster-polyline-active-border')
    window.layerRegistry.removeLayer('cluster-polyline-inactive')
    window.layerRegistry.removeLayer('cluster-polyline-active')
  },
  async loadClusterItemByIndex(store, index: number) {
    const leaves = store.state.clusterLeaves
    if (index < 0 || index >= leaves.length) return

    store.commit('setClusterActiveIndex', index)

    const leaf = leaves[index]
    const props = leaf.properties || {}
    const isCommunitySource = store.state.searchSource === 'community'
    let itemType: string
    let itemId: number

    if (isCommunitySource) {
      itemType = props.item_type || props.itemType
      itemId = Number(props.item_id || props.itemId)
    } else {
      itemType = 'Hike'
      const rawId = props.id
      const id = typeof rawId === 'string' && rawId.includes('_') ? rawId.split('_').pop() : rawId
      itemId = Number(id)
    }

    if (itemType === 'Hike' && !Number.isNaN(itemId)) {
      await store.dispatch('getTrail', itemId)
    } else if (itemType === 'Recording' && !Number.isNaN(itemId)) {
      await store.dispatch('getRecording', itemId)
    }

    // Update polyline highlighting
    store.dispatch('setAllClusterPolylinesOnMap')
  },
  navigateCluster(store, direction: 'next' | 'prev') {
    const leaves = store.state.clusterLeaves
    if (leaves.length === 0) return

    let newIndex = store.state.clusterActiveIndex
    if (direction === 'next') {
      newIndex = (newIndex + 1) % leaves.length
    } else {
      newIndex = (newIndex - 1 + leaves.length) % leaves.length
    }

    store.dispatch('loadClusterItemByIndex', newIndex)
  },
  async searchableSelected(store, searchableHit: SearchableHit) {
    let document = plainToClass(SearchableDocument, searchableHit.document)
    switch (document.getSearchableType()) {
      case SearchableType.Trail:
        await store.dispatch('getTrail', document.getItemId())
        break
      case SearchableType.Country:
        await store.dispatch('getCountry', document.getItemId())
        break
      case SearchableType.Region:
        await store.dispatch('getRegion', document.getItemId())
      case SearchableType.Park:
        window.location.href = document.url
        break
      case SearchableType.POI:
        const lngLat = [document.location[1], document.location[0]]
        window.map.flyTo({
          center: lngLat,
          zoom: 12,
          speed: 1.5, // Adjust speed of transition
          curve: 1, // Adjust smoothness of transition
          essential: true // Ensures animation is always applied
        });
    }

  }
}

function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}

const mutations: MutationTree<ExploreV2State> = {
  setTypesensePageCount(state, typesensePageCount: number) {
    state.typesensePageCount = typesensePageCount
  },
  setAnimateZoom(state, animateZoom: boolean) {
    state.animateZoom = animateZoom
  },
  setDefaultPadding(state, padding: number) {
    state.defaultPadding = padding
  },
  setTrailEncodedPolyline(state, polyline: string) {
    state.trailEncodedPolyline = polyline
  },
  setTrailEncodedPolylinePrecision(state, precision: number) {
    state.trailEncodedPolylinePrecision = precision
  },
  setRegion(state, region: any) {
    state.region = region
  },
  setCountry(state, country: any) {
    state.country = country
  },
  clearRating(state) {
    state.selectedMinRating = null
  },
  setRating(state, rating: number) {
    state.selectedMinRating = rating
  },
  removeTrailTag(state, tag: string) {
    state.selectedTrailTags.splice(state.selectedTrailTags.indexOf(tag), 1)
  },
  addTrailTag(state, tag: string) {
    state.selectedTrailTags.push(tag)
  },
  setSelectedTrailTags(state, tags: string[]) {
    state.selectedTrailTags = tags
  },
  removeCommunityItemType(state, itemType: string) {
    const idx = state.selectedCommunityItemTypes.indexOf(itemType)
    if (idx >= 0) {
      state.selectedCommunityItemTypes.splice(idx, 1)
    }
  },
  addCommunityItemType(state, itemType: string) {
    if (!state.selectedCommunityItemTypes.includes(itemType)) {
      state.selectedCommunityItemTypes.push(itemType)
    }
  },
  setSelectedCommunityItemTypes(state, itemTypes: string[]) {
    state.selectedCommunityItemTypes = itemTypes
  },
  setTrailTags(state, tags: string[]) {
    state.trailTags = tags
  },
  // Activity tags mutations
  removeActivityTag(state, tag: string) {
    state.selectedActivityTags.splice(state.selectedActivityTags.indexOf(tag), 1)
  },
  addActivityTag(state, tag: string) {
    state.selectedActivityTags.push(tag)
  },
  setSelectedActivityTags(state, tags: string[]) {
    state.selectedActivityTags = tags
  },
  setActivityTags(state, tags: string[]) {
    state.activityTags = tags
  },
  // Feature tags mutations
  removeFeatureTag(state, tag: string) {
    state.selectedFeatureTags.splice(state.selectedFeatureTags.indexOf(tag), 1)
  },
  addFeatureTag(state, tag: string) {
    state.selectedFeatureTags.push(tag)
  },
  setSelectedFeatureTags(state, tags: string[]) {
    state.selectedFeatureTags = tags
  },
  setFeatureTags(state, tags: string[]) {
    state.featureTags = tags
  },
  toggleTriggerLoading(state) {
    state.triggerLoading = !state.triggerLoading
  },
  setSearchString(state, searchString: string) {
    state.searchString = searchString
  },
  setSearchSource(state, searchSource: ExploreSearchSource) {
    state.searchSource = searchSource
  },
  setCenter(state, center: [number, number]) {
    state.center = center
  },
  setTypesenseResponse(state, typesenseResponse: TypesenseResponse) {
    state.typesenseResponse = typesenseResponse
  },
  setZoom(state, zoom: number) {
    state.zoom = zoom
  },
  setCenterAndZoom(state, centerAndZoom: { center: [number, number], zoom: number }) {
    state.center = centerAndZoom.center
    state.zoom = centerAndZoom.zoom
  },
  setDistanceRangeMax(state, mxDistance: number) {
    state.selectedDistanceRangeMax = mxDistance
  },
  setDistanceRangeMin(state, mxDistance: number) {
    state.selectedDistanceRangeMin = mxDistance
  },
  setDurationRange(state, { min, max }: { min: number, max: number | null }) {
    state.selectedDurationRangeMin = min
    state.selectedDurationRangeMax = max
  },
  addTrailGeometryType(state, type: string) {
    if (!state.selectedTrailGeometryTypes.includes(type)) {
      state.selectedTrailGeometryTypes.push(type)
    }
  },
  removeTrailGeometryType(state, type: string) {
    const idx = state.selectedTrailGeometryTypes.indexOf(type)
    if (idx >= 0) state.selectedTrailGeometryTypes.splice(idx, 1)
  },
  setSelectedTrailGeometryTypes(state, types: string[]) {
    state.selectedTrailGeometryTypes = types
  },
  setCommunityDurationRangeMin(state, minHours: number) {
    state.selectedCommunityDurationRangeMin = minHours
  },
  setCommunityDurationRangeMax(state, maxHours: number) {
    state.selectedCommunityDurationRangeMax = maxHours
  },
  setCommunityElevationRangeMin(state, minMeters: number) {
    state.selectedCommunityElevationRangeMin = minMeters
  },
  setCommunityElevationRangeMax(state, maxMeters: number) {
    state.selectedCommunityElevationRangeMax = maxMeters
  },
  restore(state, savedState) {
    Object.assign(state, savedState)
  },
  setDifficulties(state, difficulties: Difficulty[]) {
    state.selectedDifficulties = difficulties
  },
  setElevations(state, elevationDifficulties: Difficulty[]) {
    state.selectedElevations = elevationDifficulties
  },
  setTrail(state, trail: object) {
    state.trail = trail
  },
  setRecording(state, recording: object) {
    state.recording = recording
  },
  setClusterLeaves(state, leaves: any[]) {
    state.clusterLeaves = leaves
  },
  setClusterActiveIndex(state, index: number) {
    state.clusterActiveIndex = index
  },
  addClusterEncodedPolyline(state, payload: { key: string, polyline: string, precision: number }) {
    state.clusterEncodedPolylines.set(payload.key, { polyline: payload.polyline, precision: payload.precision })
  },
  setClusterNavActive(state, active: boolean) {
    state.isClusterNavActive = active
  },
  clearClusterNav(state) {
    state.clusterLeaves = []
    state.clusterActiveIndex = 0
    state.clusterEncodedPolylines = new Map()
    state.isClusterNavActive = false
  }
}

const state = (): ExploreV2State => {
  return new ExploreV2State();
};

const exploreV2Module: Module<ExploreV2State, RootState> = {
  namespaced: true,
  state: state,
  actions: actions,
  mutations: mutations,
  getters: getters,
}

export default exploreV2Module;
